import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { getApiUrl } from '../utils/api';

interface CaregiverCircle {
  id: number;
  caregiver_name: string;
  caregiver_email?: string;
  caregiver_phone?: string;
  role: string;
  is_active: boolean;
  is_primary: boolean;
  created_at: string;
}

interface CaregiverInvitation {
  id: number;
  invited_email: string;
  invited_name: string;
  invited_phone?: string;
  role: string;
  status: string;
  invitation_token: string;
  expires_at: string;
  sent_at: string;
}

interface NotificationPreference {
  id: number;
  notification_type: string;
  email_enabled: boolean;
  sms_enabled: boolean;
}

interface CustomerCaregiverCircleSummary {
  customer_id: number;
  customer_name: string;
  total_caregivers: number;
  active_caregivers: number;
  primary_caregivers: number;
  pending_invitations: number;
  caregivers: Array<{
    id: number;
    caregiver_name: string;
    role: string;
    is_active: boolean;
    is_primary: boolean;
    notification_preferences_count: number;
    pending_invitations_count: number;
  }>;
}

interface Customer {
  id: number;
  name: string;
  phone_number: string;
}

const CaregiverCircleManagement: React.FC = () => {
  const { token } = useAuth();
  const { isAdmin, isEndUser } = useRole();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | ''>('');
  const [caregiverCircles, setCaregiverCircles] = useState<CaregiverCircle[]>([]);
  const [invitations, setInvitations] = useState<CaregiverInvitation[]>([]);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreference[]>([]);
  const [summary, setSummary] = useState<CustomerCaregiverCircleSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [addCaregiverDialog, setAddCaregiverDialog] = useState(false);
  const [addInvitationDialog, setAddInvitationDialog] = useState(false);
  const [preferencesDialog, setPreferencesDialog] = useState(false);
  const [selectedCircleId, setSelectedCircleId] = useState<number | null>(null);

  // Form states
  const [caregiverForm, setCaregiverForm] = useState({
    caregiver_name: '',
    caregiver_email: '',
    caregiver_phone: '',
    role: 'secondary',
    is_primary: false
  });

  const [invitationForm, setInvitationForm] = useState({
    invited_email: '',
    invited_name: '',
    invited_phone: '',
    role: 'secondary'
  });

  const API_BASE = getApiUrl();

  useEffect(() => {
    if (isAdmin) {
      fetchCustomers();
    } else if (isEndUser) {
      // For end users, fetch their assigned customers and auto-select the first one
      fetchAssignedCustomers();
    }
  }, [isAdmin, isEndUser]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCaregiverCircles();
      fetchSummary();
    }
  }, [selectedCustomer]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_BASE}/customers/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      setError('Failed to fetch customers');
    }
  };

  const fetchAssignedCustomers = async () => {
    try {
      console.log('CaregiverCircleManagement: Fetching assigned customers with token:', token ? 'exists' : 'null');
      console.log('CaregiverCircleManagement: API URL:', API_BASE);
      
      const response = await fetch(`${API_BASE}/customers/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('CaregiverCircleManagement: Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('CaregiverCircleManagement: Customers data received:', data);
        setCustomers(data);
        // Auto-select the first assigned customer for end users
        if (data.length > 0) {
          setSelectedCustomer(data[0].id);
        }
      } else {
        const errorText = await response.text();
        console.error('CaregiverCircleManagement: API error:', response.status, errorText);
        setError('Failed to fetch assigned customers');
      }
    } catch (error) {
      console.error('CaregiverCircleManagement: Network error:', error);
      setError('Failed to fetch assigned customers');
    }
  };

  const fetchCaregiverCircles = async () => {
    if (!selectedCustomer) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/caregiver-circles/customers/${selectedCustomer}/caregivers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Deduplicate caregivers by ID to prevent duplicates
        const uniqueCaregivers = data.filter((caregiver: any, index: number, self: any[]) => 
          index === self.findIndex((c: any) => c.id === caregiver.id)
        );
        setCaregiverCircles(uniqueCaregivers);
      }
    } catch (error) {
      setError('Failed to fetch caregiver circles');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${API_BASE}/caregiver-circles/customers/${selectedCustomer}/caregivers/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      setError('Failed to fetch summary');
    }
  };

  const fetchInvitations = async (circleId: number) => {
    try {
      const response = await fetch(`${API_BASE}/caregiver-circles/caregivers/${circleId}/invitations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      setError('Failed to fetch invitations');
    }
  };

  const fetchNotificationPreferences = async (circleId: number) => {
    try {
      const response = await fetch(`${API_BASE}/caregiver-circles/caregivers/${circleId}/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationPreferences(data);
      }
    } catch (error) {
      setError('Failed to fetch notification preferences');
    }
  };

  const createCaregiverCircle = async () => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${API_BASE}/caregiver-circles/customers/${selectedCustomer}/caregivers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(caregiverForm)
      });
      
      if (response.ok) {
        setAddCaregiverDialog(false);
        setCaregiverForm({
          caregiver_name: '',
          caregiver_email: '',
          caregiver_phone: '',
          role: 'secondary',
          is_primary: false
        });
        fetchCaregiverCircles();
        fetchSummary();
      } else {
        setError('Failed to create caregiver circle');
      }
    } catch (error) {
      setError('Failed to create caregiver circle');
    }
  };

  const createInvitation = async () => {
    if (!selectedCircleId) return;
    
    try {
      const response = await fetch(`${API_BASE}/caregiver-circles/caregivers/${selectedCircleId}/invitations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invitationForm)
      });
      
      if (response.ok) {
        setAddInvitationDialog(false);
        setInvitationForm({
          invited_email: '',
          invited_name: '',
          invited_phone: '',
          role: 'secondary'
        });
        fetchInvitations(selectedCircleId);
        fetchSummary();
      } else {
        setError('Failed to create invitation');
      }
    } catch (error) {
      setError('Failed to create invitation');
    }
  };

  const deactivateCaregiverCircle = async (circleId: number) => {
    try {
      const response = await fetch(`${API_BASE}/caregiver-circles/caregivers/${circleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        fetchCaregiverCircles();
        fetchSummary();
      } else {
        setError('Failed to deactivate caregiver circle');
      }
    } catch (error) {
      setError('Failed to deactivate caregiver circle');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'primary': return 'primary';
      case 'secondary': return 'secondary';
      case 'tertiary': return 'error';
      case 'backup': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'declined': return 'error';
      case 'expired': return 'default';
      default: return 'default';
    }
  };

  const handleRefresh = () => {
    if (selectedCustomer) {
      fetchCaregiverCircles();
      fetchSummary();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Caregiver Circle Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Customer Selection - Only show for admins */}
      {isAdmin && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Customer
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Customer</InputLabel>
              <Select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value as number)}
                label="Customer"
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name} ({customer.phone_number})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Show current customer for end users */}
      {isEndUser && selectedCustomer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              My Loved One
            </Typography>
            <Typography variant="body1">
              {customers.find(c => c.id === selectedCustomer)?.name}
            </Typography>
          </CardContent>
        </Card>
      )}

      {selectedCustomer && (
        <>
          {/* Summary Card */}
          {summary && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Caregiver Circle Summary
                  </Typography>
                  <IconButton onClick={handleRefresh}>
                    <RefreshIcon />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Total Caregivers</Typography>
                    <Typography variant="h4">{summary.total_caregivers}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Active Caregivers</Typography>
                    <Typography variant="h4">{summary.active_caregivers}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Primary Caregivers</Typography>
                    <Typography variant="h4">{summary.primary_caregivers}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Pending Invitations</Typography>
                    <Typography variant="h4">{summary.pending_invitations}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Caregiver Circles */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Caregiver Circles
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddCaregiverDialog(true)}
                >
                  Add Caregiver
                </Button>
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {caregiverCircles.map((circle) => (
                    <React.Fragment key={circle.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle1">
                                {circle.caregiver_name}
                              </Typography>
                              <Chip
                                label={circle.is_primary ? "Primary" : circle.role}
                                color={circle.is_primary ? "primary" : getRoleColor(circle.role) as any}
                                size="small"
                              />
                              {!circle.is_active && (
                                <Chip label="Inactive" color="default" size="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {circle.caregiver_email && (
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                  <EmailIcon fontSize="small" />
                                  <Typography variant="body2">{circle.caregiver_email}</Typography>
                                </Box>
                              )}
                              {circle.caregiver_phone && (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <PhoneIcon fontSize="small" />
                                  <Typography variant="body2">{circle.caregiver_phone}</Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => {
                              setSelectedCircleId(circle.id);
                              fetchInvitations(circle.id);
                              setAddInvitationDialog(true);
                            }}
                            title="Send Invitation"
                          >
                            <EmailIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setSelectedCircleId(circle.id);
                              fetchNotificationPreferences(circle.id);
                              setPreferencesDialog(true);
                            }}
                            title="Notification Preferences"
                          >
                            <NotificationsIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => deactivateCaregiverCircle(circle.id)}
                            title="Deactivate"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Add Caregiver Dialog */}
      <Dialog open={addCaregiverDialog} onClose={() => setAddCaregiverDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Caregiver</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Caregiver Name"
            value={caregiverForm.caregiver_name}
            onChange={(e) => setCaregiverForm({ ...caregiverForm, caregiver_name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={caregiverForm.caregiver_email}
            onChange={(e) => setCaregiverForm({ ...caregiverForm, caregiver_email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone"
            value={caregiverForm.caregiver_phone}
            onChange={(e) => setCaregiverForm({ ...caregiverForm, caregiver_phone: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={caregiverForm.role}
              onChange={(e) => setCaregiverForm({ ...caregiverForm, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="tertiary">Tertiary</MenuItem>
              <MenuItem value="backup">Backup</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={caregiverForm.is_primary}
                onChange={(e) => setCaregiverForm({ ...caregiverForm, is_primary: e.target.checked })}
              />
            }
            label="Primary Caregiver"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCaregiverDialog(false)}>Cancel</Button>
          <Button onClick={createCaregiverCircle} variant="contained">
            Add Caregiver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Invitation Dialog */}
      <Dialog open={addInvitationDialog} onClose={() => setAddInvitationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Invitation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Invited Name"
            value={invitationForm.invited_name}
            onChange={(e) => setInvitationForm({ ...invitationForm, invited_name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={invitationForm.invited_email}
            onChange={(e) => setInvitationForm({ ...invitationForm, invited_email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Phone"
            value={invitationForm.invited_phone}
            onChange={(e) => setInvitationForm({ ...invitationForm, invited_phone: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={invitationForm.role}
              onChange={(e) => setInvitationForm({ ...invitationForm, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="tertiary">Tertiary</MenuItem>
              <MenuItem value="backup">Backup</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddInvitationDialog(false)}>Cancel</Button>
          <Button onClick={createInvitation} variant="contained">
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Preferences Dialog */}
      <Dialog open={preferencesDialog} onClose={() => setPreferencesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Notification Preferences</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Configure notification preferences for this caregiver
          </Typography>
          <List>
            {notificationPreferences.map((pref) => (
              <ListItem key={pref.id}>
                <ListItemText
                  primary={pref.notification_type.replace('_', ' ').toUpperCase()}
                  secondary="Configure how this caregiver receives notifications"
                />
                <ListItemSecondaryAction>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pref.email_enabled}
                        onChange={async (e) => {
                          try {
                            const response = await fetch(`${API_BASE}/caregiver-circles/preferences/${pref.id}`, {
                              method: 'PUT',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ email_enabled: e.target.checked })
                            });
                            if (response.ok) {
                              fetchNotificationPreferences(selectedCircleId!);
                            }
                          } catch (error) {
                            setError('Failed to update preference');
                          }
                        }}
                      />
                    }
                    label="Email"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pref.sms_enabled}
                        onChange={async (e) => {
                          try {
                            const response = await fetch(`${API_BASE}/caregiver-circles/preferences/${pref.id}`, {
                              method: 'PUT',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ sms_enabled: e.target.checked })
                            });
                            if (response.ok) {
                              fetchNotificationPreferences(selectedCircleId!);
                            }
                          } catch (error) {
                            setError('Failed to update preference');
                          }
                        }}
                      />
                    }
                    label="SMS"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreferencesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaregiverCircleManagement;
